/**
 * accountsView — Accounts S3 (docs/design/accounts-spec.md §3.2–§3.4, as
 * RULED 2026-10-24): the list's presentation over the S2 facts, pure. Every
 * fact is the server's (contracts.SalesAccountListItem); nothing here re-derives
 * R1 — single-threaded, no contact and quiet are R1's own per-deal verdicts
 * counted per account, so an account pill and a deal badge agree.
 *
 *   · Needs attention: ONE pill, by the ruled priority — duplicate →
 *     single-threaded → open deal with nobody linked → quiet → lead waiting →
 *     lower-case name → no completed activity (no-contact moved up by the
 *     ruling of 2026-10-25); none of them = "Looks healthy".
 *   · Duplicates come from their own read (GET /v1/sales/accounts/duplicates,
 *     ruling of 2026-10-25) and are attached by withDuplicates; until it
 *     answers — or on an api without it — they are UNKNOWN (null), and nothing
 *     here claims an account has none.
 *     The account pill has its own wording (not dealPeople's "One contact on
 *     a…"), and no tunable appears in copy: the money and the day counts are
 *     the account's own figures, never R1's thresholds.
 *   · Last activity is the newest COMPLETED activity (lastCompletedActivityAt),
 *     in London days — a different concept from R1's quiet, which reads only
 *     calls, emails and meetings, so the column never borrows quiet's colour.
 *   · The health card and the view counts are the TENANT-WIDE summary's
 *     (ruling of 2026-10-25), never one page's; Fix all is not here (S7).
 */
import type { DealStage, SalesAccountRow } from '../../types'
import type { SalesAccountDuplicatePair as AccountDuplicatePair, SalesAccountsSummary } from '../../../../api/types'
import { compactMoney, withNonGbpNote } from '../../../../lib/money'
import { daysBetween, londonDayOf } from './dealHealthCard'
import { londonDay } from './workingDays'
import { stageDisplay } from './homeView'

export type AttentionKind = 'duplicate' | 'singleThreaded' | 'quiet' | 'lead' | 'untidy' | 'noActivity' | 'noContact'
/** Pill colour by meaning (spec §3.4): red = duplicate; amber = single-threaded
 *  or quiet; blue = an opportunity waiting; grey = hygiene. */
export type AttentionTone = 'danger' | 'warn' | 'info' | 'neutral'

export interface AttentionPill {
  kind: AttentionKind
  tone: AttentionTone
  text: string
  /** The duplicate's confidence label (and how many more), else absent. */
  detail?: string
}

/** The ruled order (spec §3.4 as ruled 2026-10-24; no-contact after
 *  single-threaded by the ruling of 2026-10-25). */
export const ATTENTION_PRIORITY: readonly AttentionKind[] =
  ['duplicate', 'singleThreaded', 'noContact', 'quiet', 'lead', 'untidy', 'noActivity']

/** The duplicate reasons' labels (S2: same_domain and same_name are high
 *  confidence, name_prefix medium). */
export const DUPLICATE_LABEL: Record<string, string> = {
  same_domain: 'Same domain',
  same_name: 'Same name',
  name_prefix: 'Name starts the same',
}

/** A lower-case name: it has letters and none is a capital ("bell"). Mixed
 *  case is the company's own styling ("eBay") and is never flagged. */
export function isUntidyName(name: string): boolean {
  return /[a-z]/.test(name) && name === name.toLowerCase()
}

/** The Tidy suggestion: each word's first letter capitalised ("bell" → "Bell"). */
export function tidyName(name: string): string {
  return name.replace(/(^|\s)([a-z])/g, (_m, sp: string, c: string) => sp + c.toUpperCase())
}

const pillOf: Record<AttentionKind, (a: SalesAccountRow) => AttentionPill | null> = {
  duplicate: a => {
    const d = a.facts?.duplicates ?? []
    if (d.length === 0) return null
    const label = DUPLICATE_LABEL[d[0].reason] ?? 'Possible duplicate'
    return { kind: 'duplicate', tone: 'danger', text: `Possible duplicate of ${d[0].name}`, detail: d.length > 1 ? `${label} · ${d.length - 1} more` : label }
  },
  singleThreaded: a => {
    const n = a.facts?.singleThreadedDealCount ?? 0
    if (n === 0) return null
    return { kind: 'singleThreaded', tone: 'warn', text: n === 1 ? `A ${compactMoney(a.facts!.singleThreadedTopPence, 'GBP')} deal rests on one person` : `${n} deals each rest on one person` }
  },
  quiet: a => ((a.facts?.quietDealCount ?? 0) > 0
    ? { kind: 'quiet', tone: 'warn', text: `Quiet for ${a.facts!.quietDaysMax} days` } : null),
  lead: a => {
    const n = a.facts?.openLeadCount ?? 0
    if (n === 0) return null
    return { kind: 'lead', tone: 'info', text: n === 1 ? 'A lead is waiting in Inbound' : `${n} leads waiting in Inbound` }
  },
  untidy: a => (isUntidyName(a.name) ? { kind: 'untidy', tone: 'neutral', text: 'Lower-case name' } : null),
  noActivity: a => (a.facts && !a.facts.lastCompletedActivityAt ? { kind: 'noActivity', tone: 'neutral', text: 'No completed activity yet' } : null),
  noContact: a => {
    const n = a.facts?.noContactDealCount ?? 0
    if (n === 0) return null
    return { kind: 'noContact', tone: 'neutral', text: n === 1 ? 'An open deal with nobody linked' : `${n} open deals with nobody linked` }
  },
}

/** The row's one pill: the first issue by the ruled priority; null = no issue
 *  ("Looks healthy"); undefined = the wire did not describe this row — or its
 *  duplicates are unknown, so "no issue" cannot be said. */
export function attentionFor(a: SalesAccountRow): AttentionPill | null | undefined {
  if (!a.facts) return undefined
  for (const k of ATTENTION_PRIORITY) {
    const p = pillOf[k](a)
    if (p) return p
  }
  return a.facts.duplicates === null ? undefined : null
}

/** The Needs attention view (spec §3.3): a possible duplicate, a deal resting
 *  on one person, a quiet deal, or a lower-case name. */
export function needsAttention(a: SalesAccountRow): boolean {
  const f = a.facts
  if (!f) return false
  return (f.duplicates ?? []).length > 0 || f.singleThreadedDealCount > 0 || f.quietDealCount > 0 || isUntidyName(a.name)
}

const DUP_RANK: Record<string, number> = { same_domain: 0, same_name: 1, name_prefix: 2 }

/** Attach the duplicates read to the rows that carry facts: each pair to BOTH
 *  its accounts, strongest reason first, then by name. `pairs` null (the read
 *  failed, or an api without it) leaves every row's duplicates unknown. */
export function withDuplicates(rows: SalesAccountRow[], pairs: AccountDuplicatePair[] | null): SalesAccountRow[] {
  if (pairs === null) return rows
  const by = new Map<string, { accountId: string; name: string; reason: string; confidence: string }[]>()
  const put = (id: string, d: { accountId: string; name: string; reason: string; confidence: string }) => {
    const list = by.get(id) ?? []
    list.push(d)
    by.set(id, list)
  }
  for (const p of pairs) {
    put(p.accountId, { accountId: p.otherAccountId, name: p.otherName, reason: p.reason, confidence: p.confidence })
    put(p.otherAccountId, { accountId: p.accountId, name: p.accountName, reason: p.reason, confidence: p.confidence })
  }
  return rows.map(a => {
    if (!a.facts) return a
    const d = (by.get(a.id) ?? []).sort((x, y) => (DUP_RANK[x.reason] ?? 3) - (DUP_RANK[y.reason] ?? 3) || x.name.localeCompare(y.name))
    return { ...a, facts: { ...a.facts, duplicates: d } }
  })
}

/** London today, the day the activity labels count from. */
export const londonToday = (): string => londonDay(new Date())

/** The Last activity cell: "Today" · "Yesterday" · "{n} days ago" in London
 *  days from the newest COMPLETED activity; "Never" with none. */
export function lastActivityLabel(iso: string | null | undefined, today: string): string {
  const day = londonDayOf(iso)
  if (!day) return 'Never'
  const n = daysBetween(day, today)
  if (n <= 0) return 'Today'
  return n === 1 ? 'Yesterday' : `${n} days ago`
}

/** The Open pipeline sub-line: "{n} deal(s) · {furthest stage}". */
export function pipelineLine(openDealCount: number, furthest: DealStage | ''): string {
  const deals = `${openDealCount} deal${openDealCount === 1 ? '' : 's'}`
  return furthest ? `${deals} · ${stageDisplay(furthest)}` : deals
}

const listNames = (names: string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

export interface AccountHealth {
  headline: string
  single: { body: string; top: { accountId: string; name: string } | null }
  cleanups: { body: string }
  websites: { body: string; missing: number }
}

/** The Account health card (spec §3.2 as ruled) from the TENANT-WIDE summary
 *  (GET /v1/sales/accounts/summary, ruling of 2026-10-25) — never from one
 *  page of rows: the headline by rule and the three tiles. */
export function accountHealth(s: SalesAccountsSummary): AccountHealth {
  const st = s.singleThreaded
  const top = st.top ?? []
  const named = top.map(t => `${t.name} (${compactMoney(t.topPence, 'GBP')})`)
  if (st.accounts > top.length) named.push(`${st.accounts - top.length} more`)
  const singleBody = st.accounts === 0
    ? 'No deal is flagged as resting on one person.'
    : `${st.deals === 1 ? '1 deal rests on one person' : `${st.deals} deals rest on one person each`} · ${listNames(named)}. If that person leaves, the deal stalls.`

  const c = s.cleanups
  const parts: string[] = []
  if (c.firstDuplicate) parts.push(`"${c.firstDuplicate.name}" looks like a duplicate of ${c.firstDuplicate.otherName}`)
  if (c.firstLowerCase) parts.push(`"${c.firstLowerCase}" needs a capital letter`)
  const more = Math.max(0, c.duplicatePairs - 1) + Math.max(0, c.lowerCaseNames - 1)
  const cleanupsBody = parts.length === 0
    ? 'No possible duplicates or lower-case names.'
    : `${parts.join(', and ')}.${more > 0 ? ` ${more} more to review.` : ''}`

  const noWebsite = s.missingWebsites === 1 ? '1 account has no website yet' : `${s.missingWebsites} accounts have no website yet`
  const headline = st.accounts > 0
    ? (st.accounts === 1 ? '1 account has a deal resting on one person' : `${st.accounts} accounts have deals resting on one person`)
    : c.messyAccounts > 0
      ? (c.messyAccounts === 1 ? '1 account could use a tidy-up' : `${c.messyAccounts} accounts could use a tidy-up`)
      : s.missingWebsites > 0 ? noWebsite : 'Your accounts look healthy'

  return {
    headline,
    single: { body: singleBody, top: top[0] ? { accountId: top[0].accountId, name: top[0].name } : null },
    cleanups: { body: cleanupsBody },
    websites: { body: s.missingWebsites === 0 ? 'Every account has a website.' : `${noWebsite}.`, missing: s.missingWebsites },
  }
}

/** The stats line (spec §3.1) from the summary: every account of the tenant. */
export function summaryStatsLine(s: SalesAccountsSummary): string {
  const l = s.largestOpen
  const largest = l.accounts === 0 ? ''
    : ` · ${withNonGbpNote(`${compactMoney(l.pence, 'GBP')} open on the ${l.accounts === 1 ? 'largest' : `${l.accounts} largest`}`, l.excluded, 'deal')}`
  return `${s.accounts} accounts · ${s.withOpenDeals} with open deals${largest}`
}

/** The list is one page of a larger tenant: views, sorting and search act on
 *  the listed rows only, and the page says so (the cap-sentence family). Null
 *  when every account is listed or the total is not known. */
export function pageCapNote(listed: number, total: number | null): string | null {
  return total !== null && total > listed ? `From the first ${listed} accounts by name only.` : null
}
